import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes, createBrowserRouter, RouterProvider } from 'react-router-dom';
import ProtectedRoute from './shared/components/ProtectedRoute';
import RecordRoute from './shared/components/RecordRoute';
import userRoles from './utils/userRoles';
// Pages Main Components Import
import Login from './pages/login/index.jsx';
import NewEmployeeRegistration from './pages/new_employee_registration/index.jsx';
import OnboardingChecklist from './pages/onboarding_checklist/index.jsx';
import HomePage from './pages/home/index.jsx';
import CompaniesPage from './pages/companies/index.jsx';
import CompanyPage from './pages/company/index.jsx';
import ProjectsPage from './pages/projects/index.jsx';
import ProjectPage from './pages/project/index.jsx';
import PurchasesPage from './pages/purchases/index.jsx';
import PurchasePage from './pages/purchase/index.jsx';
import SalesPage from './pages/sales/index.jsx';
import SalePage from './pages/sale/index';
import DeliveriesPage from './pages/deliveries/index.jsx';
import DeliveryPage from './pages/delivery/index.jsx';
import MachinesPage from './pages/machines/index.jsx';
import MachinePage from './pages/machine/index.jsx';
import TechniciansPage from './pages/technicians/index.jsx';
import TechnicianPage from './pages/technician/index.jsx';
import ContactsPage from './pages/contacts/index.jsx';
import ContactPage from './pages/contact/index.jsx';
import ProductsPage from './pages/products/index.jsx';
import ProductPage from './pages/product/index.jsx';
import FilesPage from './pages/files/index.jsx';
import AccountPage from './pages/account/index.jsx';
import InvoicesInPage from './pages/invoices_in/index.jsx';
import InvoiceInPage from './pages/invoice_in/index.jsx';
import ApprovalQueuePage from './pages/approvals/index.jsx';
import IntakeQueuePage from './pages/approvals/intake.jsx';
import InvoicesPage from './pages/invoices/index.jsx';
import InvoicePage from './pages/invoice/index.jsx';
import RecurringInvoicesPage from './pages/recurring_invoices/index.jsx';
import RecurringInvoicePage from './pages/recurring_invoice/index.jsx';
import BankingPage from './pages/banking/index.jsx';
import QuotationsPage from './pages/quotations/index.jsx';
import QuotationPage from './pages/quotation/index.jsx';
import CreditnotesPage from './pages/creditnotes/index.jsx';
import CreditnotePage from './pages/creditnote/index.jsx';
import CreditnotesInPage from './pages/creditnotes_in/index.jsx';
import CreditnoteInPage from './pages/creditnote_in/index.jsx';
import JobsPage from './pages/jobs/index.jsx';
import JobPage from './pages/job/index';
import InquiriesPage from './pages/inquiries/index.jsx';
import InquiryPage from './pages/inquiry/index.jsx';
import HolidayRequestsComponent from './pages/holidays/index.jsx';
import AnalyticsPage from './pages/analytics/index.jsx';
import RoasthubsPage from './pages/roasthubs/index.jsx';
import CashbookPage from './pages/cashbook';
import SettingsPage from './pages/settings/index.jsx';
import SettingsUsersPage from './pages/settings/settings_details/settings_users/settingsUsers.jsx';
import SettingsUserTypesPage from './pages/settings/settings_details/settings_usertypes/settingsUserTypes.jsx';
import SettingsContactTitlesPage from './pages/settings/settings_details/settings_contacttitles/settingsContactTitles.jsx';
import SettingsPurchaseTypes from './pages/settings/settings_details/settings_purchasetypes/index.jsx';
import SettingsMachineBrands from './pages/settings/settings_details/settings_machinebrands/index.jsx';
import SettingsUnitsPage from './pages/settings/settings_details/settings_units/index.jsx';
import SettingsPubsPage from './pages/settings/settings_details/settings_pubcodes/index.jsx';
import SettingsIndustriesPage from './pages/settings/settings_details/settings_industries/index.jsx';
import StockPlacesPage from './pages/settings/settings_details/settings_pickingplaces/index.jsx';
import SettingsAgreementTypesPage from './pages/settings/settings_details/settings_agreementtypes/index.jsx';
import SettingsDocumentTypesPage from './pages/settings/settings_details/settings_documenttypes/index.jsx';
import SettingsEmailFormatsPage from './pages/settings/settings_details/settings_emailformats/index.jsx';
import SettingsPaymentCollectionsPage from './pages/settings/settings_details/settings_incasseren/index.jsx';
import SettingsQuotationsStatusesPage from './pages/settings/settings_details/settings_quotationstatuses/index.jsx';
import SettingsMaintenanceContractsPage from './pages/settings/settings_details/settings_maintenancecontracts/index.jsx';
import SettingsMachineTypesPage from './pages/settings/settings_details/settings_machinetypes/index.jsx';
import SettingsPaymentConditionsPage from './pages/settings/settings_details/settings_paymentconditions/index.jsx';
import SettingsVatShiftsPage from './pages/settings/settings_details/settings_vatshifts/index.jsx';
import SettingsOverheadPercentagesPage from './pages/settings/settings_details/settings_overheadpercentages/index.jsx';
import SettingsBusinessUnitsPage from './pages/settings/settings_details/settings_businessunits/index.jsx';
import SettingsInvoiceStatusesPage from './pages/settings/settings_details/settings_invoicestatuses/index.jsx';
import SettingsReminderTextsPage from './pages/settings/settings_details/settings_paymentreminders/index.jsx';
import MaintenanceContractsPage from './pages/maintenance_contracts';
import MaintenanceContractPage from './pages/maintenance_contract';
import MontageBonsPage from './pages/montage_bons';
import MontageBonPage from './pages/montage_bon';
import * as Sentry from '@sentry/react';
import { extraErrorDataIntegration, httpClientIntegration } from '@sentry/integrations';
import FairForm from './pages/fairs/main';
import FairsEntry from './pages/fairs';
import PageWrapper from './PageWrapper';
import { useAuthStore, useLookupStore } from './context/useStore';
import { SettingsCompanyTagsPage } from './pages/settings/settings_details/settings_companytags/index.jsx';
import { SettingsHiddenCompaniesPage } from './pages/settings/settings_details/settings_hiddencompanies/index.jsx';
import SettingsAiConnectionsPage from './pages/settings/settings_details/settings_aiconnections/index.jsx';
import { ExpensesPage } from './pages/expenses/index.jsx';
import { ExpenseForUserDetailPage } from './pages/expenseForUser/index.jsx';
import InteractionsPage from './pages/interactions/index.jsx';
// Hidden: Pipelines feature temporarily disabled
// import PipelinesPage from './pages/pipelines/index.jsx';
// import PipelinePage from './pages/pipeline/index.jsx';
import { Canvas } from './pages/Canvas';
import { CanvasPage } from './pages/Canvas/index';
import { CanvasView } from './pages/Canvas/CanvasView';
import InteractionPage from './pages/interaction/index.jsx';
import CustomerMapPage from './pages/customer_map/index.jsx';
import MachineMapPage from './pages/machine_map/index.jsx';
import ReportsPage from './pages/reports/index.jsx';
import BomCostToolPage from './pages/reports/bomCostTool.jsx';
import CabinetEstimatorPage from './pages/reports/cabinetEstimator/index.jsx';
import FleetPage from './pages/fleet/index.jsx';
// import PerfectMoosePage from './pages/perfect_moose/index.jsx';
import PerfectMooseDashboard from './pages/perfect_moose_dashboard/index.jsx';
import EmployeesPage from './pages/employees/index.jsx';
import EmployeePage from './pages/employee/index.jsx';
import InventoryPage from './pages/inventory/index.jsx';
import InventoryRedirect from './pages/inventory/redirect.jsx';
import InventoryValueOverview from './pages/inventory/valueOverview.jsx';
import CustomerQuotePage from './pages/customer_quote/index.jsx';
import { PM_DASHBOARD_USER_IDS } from './utils/pmDashboardAccess';

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: 'https://ef38dc4126ac4a50979f021b15c2540a@o534347.ingest.sentry.io/4504322151743488',
    debug: false,
    release: 'latest-release',
    environment: process.env.NODE_ENV,
    sampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    maxBreadcrumbs: 50,
    replaysSessionSampleRate: 0,
    tracesSampleRate: 0,
    sendDefaultPii: true,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({ useEffect: React.useEffect, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes }),
      Sentry.replayIntegration({ blockAllMedia: false, maskAllText: false, maskInputOptions: { password: true } }),
      extraErrorDataIntegration({ depth: 4 }),
      httpClientIntegration()
    ]
  });
}

// detail page of a record: mounted fresh when stepping to another record (see RecordRoute)
const record = (page) => <RecordRoute>{page}</RecordRoute>;

export function App() {
  const { user, setUser } = useAuthStore();
  const [initiallyLoaded, setInitiallyLoaded] = useState(false);

  const localStorageUser = JSON.parse(localStorage.getItem('currentUser'));

  useEffect(() => {
    if (localStorageUser) {
      setUser(localStorageUser);
      setInitiallyLoaded(true);
    }
  }, []);

  const {
    setAgreementtypes,
    setCompanystatuses,
    setCompanytags,
    setContacttitels,
    setCountries,
    setCurrencies,
    setDocumenttypes,
    setEmailformats,
    setIncasseren,
    setInvoicestatuses,
    setLanguages,
    setMachinebrands,
    setMachinetypes,
    setOrganisations,
    setPaymentconditions,
    setPickingplaces,
    setProjectstatuses,
    setBusinessunits,
    setPubcodes,
    setPurchasetypes,
    setRappeltexts,
    setQuotationstatuses,
    setSectors,
    setServicecontracttypes,
    setUnits,
    setUsers,
    setUsertypes,
    setVatshifts,
    setZipcodes,
    setProducttypes
  } = useLookupStore();

  // console.log('Pathname:', window.location.pathname);
  if (user && !initiallyLoaded) {
    setAgreementtypes();
    setCompanystatuses();
    setCompanytags();
    setContacttitels();
    setCountries();
    setCurrencies();
    setDocumenttypes();
    setEmailformats();
    setIncasseren();
    setInvoicestatuses();
    setLanguages();
    setMachinebrands();
    setMachinetypes();
    setOrganisations();
    setPaymentconditions();
    setPickingplaces();
    setProjectstatuses();
    setBusinessunits();
    setPubcodes();
    setPurchasetypes();
    setRappeltexts();
    setQuotationstatuses();
    setSectors();
    setServicecontracttypes();
    setUnits();
    setUsers();
    setUsertypes();
    setVatshifts();
    setZipcodes();
    setProducttypes();
    setInitiallyLoaded(true);
  }

  const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);
  const router = sentryCreateBrowserRouter([
    { path: '/login', element: <Login /> },
    { path: '/onboarding', element: <NewEmployeeRegistration /> },
    { path: '/onboarding-welcome', element: <OnboardingChecklist /> },
    { path: '/quote/:token', element: <CustomerQuotePage /> },
    {
      element: <PageWrapper />,
      children: [
        {
          element: <ProtectedRoute />,
          children: [
            { path: '/', element: <HomePage /> },
            { path: '/home', element: <HomePage /> },
            { path: '/*', element: <HomePage /> },
            { path: '/companies', element: <CompaniesPage /> },
            { path: '/companies/:id', element: record(<CompanyPage />) },
            { path: '/contacts', element: <ContactsPage /> },
            { path: '/contacts/:id', element: record(<ContactPage />) },
            { path: '/quotations', element: <QuotationsPage /> },
            { path: '/quotations/:id', element: record(<QuotationPage />) },
            { path: '/inquiries', element: <InquiriesPage /> },
            { path: '/inquiries/:id', element: record(<InquiryPage />) },
            { path: '/sales', element: <SalesPage /> },
            { path: '/sales/:id', element: record(<SalePage />) },
            { path: '/purchases', element: <PurchasesPage /> },
            { path: '/purchases/:id', element: record(<PurchasePage />) },
            { path: '/delivery_notes', element: <DeliveriesPage /> },
            { path: '/delivery_notes/:id', element: record(<DeliveryPage />) },
            { path: '/invoices', element: <InvoicesPage /> },
            { path: '/invoices/:id', element: record(<InvoicePage />) },
            { path: '/recurring_invoices', element: <RecurringInvoicesPage /> },
            { path: '/recurring_invoices/:id', element: record(<RecurringInvoicePage />) },
            { path: '/invoices_in', element: <InvoicesInPage /> },
            { path: '/invoices_in/:id', element: record(<InvoiceInPage />) },
            { path: '/approvals', element: <ApprovalQueuePage /> },
            { path: '/creditnotes', element: <CreditnotesPage /> },
            { path: '/creditnotes/:id', element: record(<CreditnotePage />) },
            { path: '/creditnotes_in', element: <CreditnotesInPage /> },
            { path: '/creditnotes_in/:id', element: record(<CreditnoteInPage />) },
            { path: '/products', element: <ProductsPage /> },
            { path: '/products/:id', element: record(<ProductPage />) },
            { path: '/inventory', element: <InventoryRedirect /> },
            { path: '/inventory/value', element: <InventoryValueOverview /> },
            { path: '/inventory/:stockplaatsId', element: <InventoryPage /> },
            { path: '/projects', element: <ProjectsPage /> },
            { path: '/projects/:id', element: record(<ProjectPage />) },
            { path: '/jobs', element: <JobsPage /> },
            { path: '/jobs/:id', element: record(<JobPage />) },
            { path: '/montage_bons', element: <MontageBonsPage /> },
            { path: '/montage_bons/:id', element: record(<MontageBonPage />) },
            // { path: '/perfect_moose', element: <PerfectMoosePage /> },
            { path: '/machines', element: <MachinesPage /> },
            { path: '/machines/:id', element: record(<MachinePage />) },
            { path: '/maintenance_contracts', element: <MaintenanceContractsPage /> },
            { path: '/maintenance_contracts/:id', element: record(<MaintenanceContractPage />) },
            { path: '/technicians', element: <TechniciansPage /> },
            { path: '/technicians/:id', element: record(<TechnicianPage />) },
            { path: '/cashbook', element: <CashbookPage /> },
            { path: '/holidays', element: <HolidayRequestsComponent /> },
            { path: '/account', element: <AccountPage /> },
            { path: '/files', element: <FilesPage /> },
            { path: '/analytics', element: <AnalyticsPage /> },
            { path: '/roasthubs', element: <RoasthubsPage /> },
            { path: '/interactions', element: <InteractionsPage /> },
            { path: '/interactions/:id', element: record(<InteractionPage />) },
            // Hidden: Pipelines feature temporarily disabled
            // { path: '/pipelines', element: <PipelinesPage /> },
            // { path: '/pipelines/:id', element: <PipelinePage /> },
            { path: '/canvas', element: <CanvasPage /> },
            { path: '/canvas/:id', element: <CanvasView /> },
            { path: '/customer-map', element: <CustomerMapPage /> },
            { path: '/machine-map', element: <MachineMapPage /> },
            { path: '/reports', element: <ReportsPage /> },
            { path: '/reports/bom-tool', element: <BomCostToolPage /> },
            { path: '/reports/cabinet-estimator', element: <CabinetEstimatorPage /> }
          ]
        },
        {
          element: <ProtectedRoute userIds={PM_DASHBOARD_USER_IDS} />,
          children: [{ path: '/pm-dashboard', element: <PerfectMooseDashboard /> }]
        },
        {
          element: <ProtectedRoute roles={[userRoles.AdminFull, userRoles.AdminData]} />,
          children: [
            { path: '/employees', element: <EmployeesPage /> },
            { path: '/employees/:id', element: record(<EmployeePage />) },
            { path: '/approvals/intake', element: <IntakeQueuePage /> }
          ]
        },
        {
          element: <ProtectedRoute flag="bankingAccess" />,
          children: [{ path: '/banking', element: <BankingPage /> }]
        },
        {
          element: <ProtectedRoute roles={[userRoles.AdminFull]} />,
          children: [
            { path: '/fleet', element: <FleetPage /> },
            { path: '/settings', element: <SettingsPage /> },
            { path: '/settings/agreement_types', element: <SettingsAgreementTypesPage /> },
            { path: '/settings/companytags', element: <SettingsCompanyTagsPage /> },
            { path: '/settings/hidden_companies', element: <SettingsHiddenCompaniesPage /> },
            { path: '/settings/ai_connections', element: <SettingsAiConnectionsPage /> },
            { path: '/settings/contacttitels', element: <SettingsContactTitlesPage /> },
            { path: '/settings/documenttypes', element: <SettingsDocumentTypesPage /> },
            { path: '/settings/emailformats', element: <SettingsEmailFormatsPage /> },
            { path: '/settings/invoicestatuses', element: <SettingsInvoiceStatusesPage /> },
            { path: '/settings/machinebrands', element: <SettingsMachineBrands /> },
            { path: '/settings/machinetypes', element: <SettingsMachineTypesPage /> },
            { path: '/settings/paymentcollections', element: <SettingsPaymentCollectionsPage /> },
            { path: '/settings/paymentconditions', element: <SettingsPaymentConditionsPage /> },
            { path: '/settings/pickingplaces', element: <StockPlacesPage /> },
            { path: '/settings/pubCodes', element: <SettingsPubsPage /> },
            { path: '/settings/purchasetypes', element: <SettingsPurchaseTypes /> },
            { path: '/settings/business_units', element: <SettingsBusinessUnitsPage /> },
            { path: '/settings/quotationstatuses', element: <SettingsQuotationsStatusesPage /> },
            { path: '/settings/remindertexts', element: <SettingsReminderTextsPage /> },
            { path: '/settings/sectors', element: <SettingsIndustriesPage /> },
            { path: '/settings/service_contracttypes', element: <SettingsMaintenanceContractsPage /> },
            { path: '/settings/units', element: <SettingsUnitsPage /> },
            { path: '/settings/users', element: <SettingsUsersPage /> },
            { path: '/settings/usertypes', element: <SettingsUserTypesPage /> },
            { path: '/settings/vatshifts', element: <SettingsVatShiftsPage /> },
            { path: '/settings/overhead_percentages', element: <SettingsOverheadPercentagesPage /> },
            { path: '/expenses', element: <ExpensesPage /> },
            { path: '/expenses/:userId', element: <ExpenseForUserDetailPage /> }
          ]
        }
      ]
    },
    {
      element: <ProtectedRoute />,
      children: [
        { path: '/fairs/*', element: <FairsEntry /> },
        { path: '/fairs/form', element: <FairForm /> }
      ]
    }
  ]);

  return <RouterProvider router={router} />;
}

const container = document.getElementById('root');
const root = createRoot(container);

root.render(<App />);
